import { ExclamationCircleFilled, MobileOutlined } from "@ant-design/icons";
import {
  Modal, Spin, Row, Col, Card, Typography, Button, Space, Avatar, Tag, Tour, FloatButton,
} from "antd";
import {
  PlusCircleOutlined, WalletOutlined, AppstoreOutlined, PlayCircleOutlined,
  ArrowRightOutlined, ClockCircleOutlined, RocketOutlined, BankOutlined,
  FileAddOutlined, UnorderedListOutlined, AuditOutlined, DollarOutlined,
  UserAddOutlined, TeamOutlined, IdcardOutlined, PercentageOutlined,
  SafetyCertificateOutlined, SyncOutlined, HistoryOutlined, SwapOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import NavBar from "../components/NavBar";
import SideBar from "../components/SideBar";
import { LOGGED_USER } from "../variables/constants";
import CompanySelectorModal from "../components/CompanySelectorModal";
import axiosInstance from "../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { mockData } from "../utils/navbarUtils";
import {
  evaluatePasswordChangeRequirement,
  PASSWORD_CHANGE_REASON,
} from "../utils/passwordChangePolicy";
import { useDernierAcces } from "../hooks/UserDernierAcces";
import { COLORS } from "../components/ui/colors";
import { rightsService } from "../services/rightsService";
import HomeDashboard from "./home/HomeDashboard";

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

// Accès rapides par PROFIL (détection par mot-clé dans le nom du profil) — volontairement
// courts pour rester épuré. Chaque lien porte le(s) code(s) d'habilitation requis :
//   right: "CODE"           → habilitation unique
//   right: ["A", "B"]       → au moins une des habilitations (logique OU)
//   pas de champ right      → toujours visible (aucune habilitation requise)
const LINKS_PRODUCTEUR = [
  { key: "an", label: "Nouvelle affaire", desc: "Souscrire un contrat", icon: <PlusCircleOutlined />, color: "#1890ff", to: "/ajouter-contrat-new", right: ["HCO005", "HCO022"] },
  { key: "enc", label: "Encaissements", desc: "Régler les primes", icon: <WalletOutlined />, color: "#52c41a", to: "/encaissements", right: "HCO008" },
  { key: "prod", label: "Production", desc: "Suivre les contrats", icon: <AppstoreOutlined />, color: "#722ed1", to: "/production", right: "HCO020" },
  { key: "rev", label: "Reversements", desc: "Bordereaux compagnie", icon: <BankOutlined />, color: "#fa8c16", to: "/versements-en-compagnie", right: "HCO008" },
  { key: "tuto", label: "Tutoriels", desc: "Centre de formation", icon: <PlayCircleOutlined />, color: "#eb2f96", to: "/video-tutorials" },
];

const LINKS_SINISTRE = [
  { key: "decl", label: "Déclarer un sinistre", desc: "Nouvelle déclaration", icon: <FileAddOutlined />, color: "#ff4d4f", to: "/declarer-sinister", right: "HSI027" },
  { key: "list", label: "Liste des sinistres", desc: "Dossiers en cours", icon: <UnorderedListOutlined />, color: "#1890ff", to: "/liste-des-sinistres", right: "HSI027" },
  { key: "eval", label: "Évaluations", desc: "Valider les évaluations", icon: <AuditOutlined />, color: "#722ed1", to: "/validation-evaluation", right: "HSI027" },
  { key: "paie", label: "Paiements sinistres", desc: "Valider les décaissements", icon: <DollarOutlined />, color: "#52c41a", to: "/validation-decaissement", right: "HSI027" },
];

// Admin d'une compagnie (NON-Pool) : gestion membres + attestations + journal.
const LINKS_ADMIN = [
  { key: "newuser", label: "Nouvel utilisateur", desc: "Créer un compte", icon: <UserAddOutlined />, color: "#1890ff", to: "/ajouter-utilisateur", right: "HPE033" },
  { key: "users", label: "Utilisateurs", desc: "Liste des comptes", icon: <TeamOutlined />, color: "#52c41a", to: "/utilisateurs", right: "HPE033" },
  { key: "interm", label: "Intermédiaires", desc: "Liste des intermédiaires", icon: <IdcardOutlined />, color: "#722ed1", to: "/intermediaires", right: "HME030" },
  { key: "court", label: "Courtiers", desc: "Liste des courtiers", icon: <BankOutlined />, color: "#fa8c16", to: "/courtiers", right: "HME030" },
  { key: "taux", label: "Taux de commissions", desc: "Paramétrage", icon: <PercentageOutlined />, color: "#eb2f96", to: "/parametres/taux-commissions", right: "HPA031" },
  { key: "attest", label: "Les attestations", desc: "Gestion des attestations", icon: <SafetyCertificateOutlined />, color: "#13c2c2", to: "/mes-attestations", right: "HCO069" },
  { key: "logs", label: "Journal d'activités", desc: "Traçabilité", icon: <HistoryOutlined />, color: "#595959", to: "/activity-logs", right: "HPE033" },
];

// SuperAdmin / Admin au POOL : pilotage central (utilisateurs, attestations, synchro,
// versements des agences STCA/DTRF, journal d'activités).
const LINKS_ADMIN_POOL = [
  { key: "newuser", label: "Nouvel utilisateur", desc: "Créer un compte", icon: <UserAddOutlined />, color: "#1890ff", to: "/ajouter-utilisateur", right: "HPE033" },
  { key: "users", label: "Utilisateurs", desc: "Liste des comptes", icon: <TeamOutlined />, color: "#52c41a", to: "/utilisateurs", right: "HPE033" },
  { key: "attest", label: "Les attestations", desc: "Gestion des attestations", icon: <SafetyCertificateOutlined />, color: "#13c2c2", to: "/gestion-des-attestations", right: "HCO069" },
  { key: "sync", label: "Synchronisation", desc: "Suivi des synchros", icon: <SyncOutlined />, color: "#722ed1", to: "/synchronisations", right: "HLA083" },
  { key: "versAg", label: "Versements agences", desc: "STCA / DTRF", icon: <SwapOutlined />, color: "#fa8c16", to: "/vt/versements-bancaires", right: "HLA086" },
  { key: "logs", label: "Journal d'activités", desc: "Traçabilité", icon: <HistoryOutlined />, color: "#595959", to: "/activity-logs", right: "HPE033" },
];

// Habilité ? Aucun droit requis → oui ; tableau → au moins un ; sinon → le code exact.
// rightsService inclut déjà les dérogations actives.
const estHabilite = (link) => {
  if (!link?.right) return true;
  if (Array.isArray(link.right)) return link.right.some((code) => rightsService(code));
  return rightsService(link.right);
};

// Sélection des accès rapides selon le nom du profil (insensible à la casse) et,
// pour les admins, selon l'appartenance au POOL ; puis filtrage par habilitation.
const getQuickLinks = (profilNom, isPool) => {
  const p = (profilNom || "").toLowerCase();
  let links = [];
  if (p.includes("sinistre")) links = LINKS_SINISTRE;
  else if (p.includes("produc")) links = LINKS_PRODUCTEUR;
  else if (p.includes("admin")) links = isPool ? LINKS_ADMIN_POOL : LINKS_ADMIN;
  return links.filter(estHabilite);
};

// Date du jour en français, sans dépendance de locale dayjs.
const dateDuJour = () => {
  try {
    const s = new Intl.DateTimeFormat("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    }).format(new Date());
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch (_) {
    return "";
  }
};

const salutation = () => {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
};

const HomeV2 = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const { dernierAcces, loading: loadingReprise, reprendre } = useDernierAcces();
  const askedPasswordChangeRef = useRef(false);

  const prenom = LOGGED_USER?.personne?.prenom || "";
  const nom = LOGGED_USER?.personne?.nom || "";
  const initiales = `${(prenom[0] || "").toUpperCase()}${(nom[0] || "").toUpperCase()}` || "U";
  const organisme = LOGGED_USER?.profil?.organisme?.raisonSociale;
  const agence = LOGGED_USER?.agence?.nom;

  // Accès rapides selon le profil (+ appartenance Pool pour les admins) ; le 1er lien
  // sert aussi de CTA principal du bandeau.
  const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
  const quickLinks = getQuickLinks(LOGGED_USER?.profil?.nom, isPoolUser);
  const ctaPrincipal = quickLinks[0] || null;
  // Tableau de bord pour les profils admin / producteur / sinistre (le contenu s'adapte
  // au profil côté backend) ET soumis à l'habilitation États/Stats HET023 :
  // seuls les utilisateurs habilités le voient.
  const profilNomLc = (LOGGED_USER?.profil?.nom || "").toLowerCase();
  const showDashboard =
    (profilNomLc.includes("admin") || profilNomLc.includes("produc") || profilNomLc.includes("sinistre"))
    && rightsService("HET023");

  // ── Tour guidé (adapté au profil et aux habilitations) ──────────────
  const [openTour, setOpenTour] = useState(() => localStorage.getItem("homeV2TourSeen") !== "1");
  const closeTour = () => { setOpenTour(false); localStorage.setItem("homeV2TourSeen", "1"); };
  const refHero = useRef(null);
  const refQuickLinks = useRef(null);
  const refDashboard = useRef(null);

  // Libellé des accès rapides selon le profil détecté.
  const profilLabel = profilNomLc.includes("sinistre")
    ? "sinistres (déclarer, suivre, valider les paiements…)"
    : profilNomLc.includes("produc")
      ? "production (nouvelle affaire, contrats, taux…)"
      : profilNomLc.includes("admin")
        ? (isPoolUser
          ? "d'administration Pool (utilisateurs, attestations, synchronisation, versements, journal d'activités…)"
          : "d'administration (utilisateurs, intermédiaires, courtiers, taux de commissions…)")
        : "adaptés à votre profil";

  const tourSteps = [
    {
      title: "Bienvenue sur votre nouvelle page d'accueil ✨",
      description: `Votre espace PoolIns a été repensé : plus moderne, plus clair et personnalisé selon votre profil${LOGGED_USER?.profil?.nom ? ` « ${LOGGED_USER.profil.nom} »` : ""}. Laissez-vous guider en quelques étapes.`,
      target: null,
      nextButtonProps: { children: "Commencer" },
    },
    {
      title: "Votre bandeau d'accueil",
      description: "Retrouvez ici votre salutation, votre espace (organisme/agence) et un bouton pour reprendre directement votre dernier dossier consulté.",
      target: () => refHero.current,
      nextButtonProps: { children: "Suivant" },
      prevButtonProps: { children: "Précédent" },
    },
  ];
  if (quickLinks.length > 0) {
    tourSteps.push({
      title: "Vos accès rapides",
      description: `Des raccourcis vers vos actions ${profilLabel}. Seuls ceux autorisés par vos habilitations apparaissent.`,
      target: () => refQuickLinks.current,
      nextButtonProps: { children: "Suivant" },
      prevButtonProps: { children: "Précédent" },
    });
  }
  if (showDashboard) {
    tourSteps.push({
      title: "Votre tableau de bord",
      description: "Vos statistiques clés (activité du jour/semaine/mois/exercice) en un coup d'œil. Visible car vous disposez de l'habilitation États & statistiques.",
      target: () => refDashboard.current,
      nextButtonProps: { children: "Terminer" },
      prevButtonProps: { children: "Précédent" },
    });
  } else {
    // Dernière étape sans dashboard → bouton "Terminer" sur la dernière étape présente.
    tourSteps[tourSteps.length - 1].nextButtonProps = { children: "Terminer" };
  }

  useEffect(() => {
    const decision = evaluatePasswordChangeRequirement(LOGGED_USER);
    if (decision.shouldForce && !askedPasswordChangeRef.current) {
      askedPasswordChangeRef.current = true;
      confirmPasswordChange(decision.reason);
    }

    if (LOGGED_USER?.agence?.typeAgence === "COURTIER") {
      setVisible(true);
      const codes = LOGGED_USER?.agence?.validAgrements?.map((ag) => ag?.compagnie?.code) ?? [];
      axiosInstance
        .get(`/api/organisme/all/assurances`)
        .then((res) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          setCompanies(data.filter((comp) => codes.includes(comp?.code)));
          setLoading(false);
        })
        .catch((err) => {
          console.error("Erreur lors du chargement des compagnies:", err);
          setCompanies([]);
          setLoading(false);
        });
    } else {
      setLoading(false);
      setVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompanySelect = (company) => {
    toast(`Compagnie ${company?.raisonSociale} sélectionnée avec succès!`, { position: "top-right", autoClose: 5000, theme: "light" });
    sessionStorage.setItem("currentCompagnie", JSON.stringify(company));
    setVisible(false);
  };

  const confirmPasswordChange = (reason) => {
    const content =
      reason === PASSWORD_CHANGE_REASON.EXPIRED
        ? "Votre mot de passe est expiré. Vous devez en définir un nouveau maintenant."
        : "Pour des raisons de sécurité, vous devez définir un nouveau mot de passe maintenant.";
    confirm({
      title: "Modification du mot de passe requise",
      icon: <ExclamationCircleFilled />,
      content,
      okText: "Changer mon mot de passe",
      okType: "primary",
      okCancel: false,
      maskClosable: false,
      keyboard: false,
      centered: true,
      onOk() { navigate("/first-connexion"); },
    });
  };

  const renderSearchResults = (results) =>
    results.map(({ category, items }) => (
      <div key={category} className="px-3 py-2">
        <h6 className="text-muted text-uppercase mb-2">{category}</h6>
        {items
          .filter((item) => item.name.toLowerCase().includes(searchValue.toLowerCase()))
          .map((item, index) => (
            <a key={index} href={item.url} className="dropdown-item d-flex align-items-center gap-2 py-1">
              <i className={`bx ${item.icon} text-muted`}></i>
              <span className="text-truncate">{item.name}</span>
            </a>
          ))}
      </div>
    ));

  useEffect(() => {
    if (searchValue && searchValue.length > 2) {
      const results = [];
      Object.keys(mockData).forEach((key) => {
        const filtered = mockData[key].filter((item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase())
        );
        if (filtered.length) results.push({ category: key, items: filtered });
      });
      setSearchResults(results);
    }
  }, [searchValue]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"DASHBOARD"} />
        <div className="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* ── Hero ────────────────────────────────────────────────── */}
              <Card
                ref={refHero}
                bordered={false}
                style={{
                  borderRadius: 20,
                  marginBottom: 24,
                  background: COLORS.gradient,
                  boxShadow: "0 12px 30px rgba(36, 33, 102, 0.25)",
                  overflow: "hidden",
                }}
                bodyStyle={{ padding: "32px 36px" }}
              >
                <Row align="middle" gutter={[24, 24]} justify="space-between">
                  <Col xs={24} md={16}>
                    <Space size={16} align="center" style={{ marginBottom: 14 }}>
                      <Avatar size={56} style={{ background: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: 20 }}>
                        {initiales}
                      </Avatar>
                      <div>
                        <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
                          <ClockCircleOutlined style={{ marginRight: 6 }} />
                          {dateDuJour()}
                        </Text>
                        <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700, lineHeight: 1.15 }}>
                          {salutation()}, {prenom || nom} 👋🏾
                        </Title>
                      </div>
                    </Space>
                    <Paragraph style={{ color: "rgba(255,255,255,0.9)", fontSize: 15, maxWidth: 540, marginBottom: 18 }}>
                      Bienvenue sur PoolIns. Tout ce qu'il vous faut pour vos opérations, au même endroit.
                    </Paragraph>
                    <Space wrap size={12}>
                      <Button
                        type="default"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={reprendre}
                        disabled={loadingReprise || !dernierAcces}
                        title={dernierAcces?.libelle ?? "Aucun dossier récent"}
                        style={{ borderRadius: 10, fontWeight: 600, border: "none" }}
                      >
                        {loadingReprise ? "Chargement…" : dernierAcces ? "Reprendre le dernier dossier" : "Aucun dossier récent"}
                      </Button>
                      {ctaPrincipal && (
                        <Button
                          type="primary"
                          size="large"
                          ghost
                          icon={ctaPrincipal.icon}
                          onClick={() => navigate(ctaPrincipal.to)}
                          style={{ borderRadius: 10, fontWeight: 600, color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
                        >
                          {ctaPrincipal.label}
                        </Button>
                      )}
                      <Button
                        type="text"
                        size="large"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setOpenTour(true)}
                        style={{ borderRadius: 10, fontWeight: 600, color: "rgba(255,255,255,0.92)" }}
                      >
                        Guide
                      </Button>
                    </Space>
                  </Col>
                  <Col xs={0} md={8} style={{ textAlign: "right" }}>
                    {(organisme || agence) && (
                      <div style={{
                        background: "rgba(255,255,255,0.12)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        borderRadius: 16,
                        padding: "18px 20px",
                        display: "inline-block",
                        textAlign: "left",
                        backdropFilter: "blur(6px)",
                      }}>
                        <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
                          Votre espace
                        </Text>
                        <div style={{ color: "#fff", fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                          <BankOutlined style={{ marginRight: 8 }} />
                          {organisme || "—"}
                        </div>
                        {agence && (
                          <Tag style={{ marginTop: 8, borderRadius: 8, border: "none", background: "rgba(255,255,255,0.2)", color: "#fff" }}>
                            {agence}
                          </Tag>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>
              </Card>

              {/* ── Accès rapides (selon le profil) ─────────────────────── */}
              {quickLinks.length > 0 && (
              <div ref={refQuickLinks}>
              <Text strong style={{ display: "block", marginBottom: 14, fontSize: 15, color: COLORS.secondary }}>
                Accès rapides
              </Text>
              <Row gutter={[20, 20]}>
                {quickLinks.map((q) => (
                  <Col xs={12} sm={12} md={8} lg={Math.floor(24 / Math.min(quickLinks.length, 5)) || 6} key={q.key}>
                    <Card
                      hoverable
                      bordered={false}
                      onClick={() => navigate(q.to)}
                      style={{ borderRadius: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", height: "100%" }}
                      bodyStyle={{ padding: 22 }}
                    >
                      <div style={{
                        width: 48, height: 48, borderRadius: 12,
                        background: `${q.color}15`, color: q.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 24, marginBottom: 14,
                      }}>
                        {q.icon}
                      </div>
                      <div style={{ fontWeight: 700, color: COLORS.secondary, fontSize: 15 }}>{q.label}</div>
                      <Text type="secondary" style={{ fontSize: 12.5 }}>{q.desc}</Text>
                      <div style={{ marginTop: 12, color: q.color, fontWeight: 600, fontSize: 12.5 }}>
                        Ouvrir <ArrowRightOutlined style={{ fontSize: 11 }} />
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
              </div>
              )}

              {/* ── Tableau de bord (admin / producteur / sinistre) ─────── */}
              {showDashboard && (
                <div ref={refDashboard}>
                  <HomeDashboard isPool={isPoolUser} />
                </div>
              )}
            </div>
            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>

      {/* Sélecteur de compagnie (courtier) */}
      {loading ? (
        <Modal title="Chargement..." open={visible} closable={false} footer={null} centered>
          <Spin size="large" />
        </Modal>
      ) : (
        <CompanySelectorModal visible={visible} companies={companies} onSelect={handleCompanySelect} />
      )}

      {/* Le tour attend la fin du chargement / de la sélection de compagnie. */}
      <Tour open={openTour && !loading && !visible} onClose={closeTour} steps={tourSteps} />

      {/* Raccourci flottant vers la page publique de téléchargement de l'application mobile.
          Réservé aux profils ADMIN : on réutilise `profilNomLc` (déjà calculé plus haut et
          servant au même type de test ailleurs dans cette page). */}
      {profilNomLc.includes("admin") && (
        <FloatButton
          icon={<MobileOutlined />}
          type="primary"
          tooltip="Télécharger l'application mobile"
          onClick={() => navigate("/download")}
          style={{ right: 32, bottom: 32 }}
        />
      )}

      <div className="layout-overlay layout-menu-toggle show"></div>
    </div>
  );
};

export default HomeV2;
